import { useState } from "react"
import { Permission, type RoleWithPermissions } from "@mc/types"
import { Button } from "../ui/Button.js"
import { Input } from "../ui/Input.js"

/** Grouped for the checklist UI, mirrors the section comments in
 *  @mc/types's permissions.ts (Lifecycle, Console/logs, Files, ...) so the
 *  role builder reads the same way the source of truth is organized. */
const PERMISSION_GROUPS: { label: string; permissions: Permission[] }[] = [
  { label: "Ciclo de vida", permissions: [Permission.SERVER_START, Permission.SERVER_STOP, Permission.SERVER_RESTART, Permission.SERVER_KILL] },
  { label: "Consola y logs", permissions: [Permission.CONSOLE_READ, Permission.CONSOLE_WRITE, Permission.LOGS_READ] },
  { label: "Archivos", permissions: [Permission.FILES_READ, Permission.FILES_WRITE, Permission.FILES_DELETE] },
  { label: "Mods y plugins", permissions: [Permission.MODS_INSTALL, Permission.MODS_DELETE, Permission.MODS_UPDATE] },
  { label: "Configuración", permissions: [Permission.CONFIG_READ, Permission.CONFIG_WRITE] },
  { label: "Backups", permissions: [Permission.BACKUPS_CREATE, Permission.BACKUPS_RESTORE, Permission.BACKUPS_DELETE] },
  { label: "Jugadores", permissions: [Permission.PLAYERS_KICK, Permission.PLAYERS_BAN, Permission.PLAYERS_WHITELIST, Permission.PLAYERS_OP] },
  { label: "Ajustes", permissions: [Permission.SETTINGS_READ, Permission.SETTINGS_WRITE] },
  { label: "Acceso", permissions: [Permission.ACCESS_MANAGE, Permission.SERVER_DELETE] },
]

const PERMISSION_LABELS: Record<Permission, string> = {
  [Permission.SERVER_START]: "Iniciar",
  [Permission.SERVER_STOP]: "Detener",
  [Permission.SERVER_RESTART]: "Reiniciar",
  [Permission.SERVER_KILL]: "Forzar apagado",
  [Permission.CONSOLE_READ]: "Ver consola",
  [Permission.CONSOLE_WRITE]: "Escribir en consola",
  [Permission.LOGS_READ]: "Ver logs",
  [Permission.FILES_READ]: "Ver archivos",
  [Permission.FILES_WRITE]: "Editar archivos",
  [Permission.FILES_DELETE]: "Borrar archivos",
  [Permission.MODS_INSTALL]: "Instalar mods",
  [Permission.MODS_DELETE]: "Borrar mods",
  [Permission.MODS_UPDATE]: "Actualizar mods",
  [Permission.CONFIG_READ]: "Ver configuración",
  [Permission.CONFIG_WRITE]: "Editar configuración",
  [Permission.BACKUPS_CREATE]: "Crear backups",
  [Permission.BACKUPS_RESTORE]: "Restaurar backups",
  [Permission.BACKUPS_DELETE]: "Borrar backups",
  [Permission.PLAYERS_KICK]: "Expulsar jugadores",
  [Permission.PLAYERS_BAN]: "Banear jugadores",
  [Permission.PLAYERS_WHITELIST]: "Editar whitelist",
  [Permission.PLAYERS_OP]: "Dar/quitar OP",
  [Permission.SETTINGS_READ]: "Ver ajustes",
  [Permission.SETTINGS_WRITE]: "Editar ajustes",
  [Permission.ACCESS_MANAGE]: "Gestionar acceso",
  [Permission.SERVER_DELETE]: "Borrar servidor",
  [Permission.ADMIN_CONFIG_WRITE]: "Config. de sistema (host_owner)",
}

const DEFAULT_COLOR = "#6366f1"

interface RoleEditorProps {
  /** null = creating a new role. */
  role: RoleWithPermissions | null
  onSave: (input: { name: string; color: string; permissions: Permission[] }) => Promise<void>
  onCancel: () => void
}

/**
 * Free-form Discord-style role builder (FR section 3), shared between
 * server-scoped roles (ServerAccessPage) and family-scoped roles
 * (FamilyDetailPage) — same shape, same @mc/db functions underneath, only
 * the scopeId differs, resolved server-side per route.
 */
export function RoleEditor({ role, onSave, onCancel }: RoleEditorProps) {
  const [name, setName] = useState(role?.name ?? "")
  const [color, setColor] = useState(role?.color ?? DEFAULT_COLOR)
  const [permissions, setPermissions] = useState<Set<Permission>>(new Set(role?.permissions ?? []))
  const [saving, setSaving] = useState(false)

  const togglePermission = (permission: Permission) => {
    setPermissions((prev) => {
      const next = new Set(prev)
      if (next.has(permission)) next.delete(permission)
      else next.add(permission)
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave({ name, color, permissions: Array.from(permissions) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
        padding: "var(--space-4)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--brand-primary)",
        background: "var(--bg-surface-raised)",
      }}
    >
      <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 220px" }}>
          <Input label="Nombre del rol" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)", display: "block", marginBottom: "var(--space-2)" }}>
            Color
          </label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ width: 48, height: 40, border: "1px solid var(--border-default)", borderRadius: "var(--radius-sm)", background: "none" }}
          />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {PERMISSION_GROUPS.map((group) => (
          <div key={group.label}>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {group.label}
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", marginTop: "var(--space-1)" }}>
              {group.permissions.map((permission) => {
                const checked = permissions.has(permission)
                return (
                  <label
                    key={permission}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-1)",
                      fontSize: "var(--text-xs)",
                      padding: "4px var(--space-2)",
                      borderRadius: "var(--radius-sm)",
                      border: `1px solid ${checked ? "var(--brand-primary)" : "var(--border-default)"}`,
                      background: checked ? "var(--brand-subtle)" : "transparent",
                      color: checked ? "var(--brand-primary)" : "var(--text-primary)",
                      cursor: "pointer",
                      transition: "background-color var(--duration-small) var(--ease-out), border-color var(--duration-small) var(--ease-out)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePermission(permission)}
                      style={{ margin: 0 }}
                    />
                    {PERMISSION_LABELS[permission]}
                  </label>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "var(--space-2)" }}>
        <Button loading={saving} disabled={!name} onClick={handleSave}>
          Guardar rol
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}
